import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { InlineSpinner } from '@/components/ui/loading-spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { Student } from '@/types';
import { studentApi, classApi } from '@/lib/api';
import { Search, UserPlus, Users, UserMinus } from 'lucide-react';

interface StudentAssignmentProps {
  classId: string;
  batchId: string; // Changed from academicYearId to batchId
  initialStudentIds?: string[];
  onAssignmentChange?: (studentIds: string[]) => void;
  onAssignmentSaved?: () => void; // Callback after successful save
}

const StudentAssignment = ({
  classId,
  batchId, // Changed from academicYearId to batchId
  initialStudentIds = [],
  onAssignmentChange,
  onAssignmentSaved
}: StudentAssignmentProps) => {
  const { toast } = useToast();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [savedStudentIds, setSavedStudentIds] = useState<string[]>([]); // Track the last saved state
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{
    added: string[];
    removed: string[];
  }>({ added: [], removed: [] });
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch students for the batch
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const data = await studentApi.getAll(batchId);
        setStudents(data);
      } catch (error) {
        console.error('Error fetching students:', error);
        toast({
          title: 'Error',
          description: 'Failed to load students. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    if (batchId) {
      fetchStudents();
    }
  }, [batchId, toast]);

  // Initialize saved state when initial student IDs change
  useEffect(() => {
    // Only initialize once when we first get valid initialStudentIds
    if (!isInitialized && initialStudentIds && initialStudentIds.length >= 0) {
      const safeInitialIds = Array.isArray(initialStudentIds) ? initialStudentIds : [];
      setSelectedStudentIds(safeInitialIds);
      setSavedStudentIds(safeInitialIds);
      setPendingChanges({ added: [], removed: [] });
      setIsInitialized(true);
    }
  }, [initialStudentIds, isInitialized]);

  // Calculate pending changes for display purposes only
  const calculatePendingChanges = () => {
    const added = selectedStudentIds.filter(id => !savedStudentIds.includes(id));
    const removed = savedStudentIds.filter(id => !selectedStudentIds.includes(id));
    return { added, removed };
  };

  // Get current pending changes for display
  const currentPendingChanges = calculatePendingChanges();



  // Filter students based on search term
  const filteredStudents = students.filter(student => {
    const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      student.email.toLowerCase().includes(searchLower) ||
      student.studentId.toLowerCase().includes(searchLower)
    );
  });

  // Toggle student selection
  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds(prev => {
      const newSelection = prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId];

      // Notify parent component if callback provided
      if (onAssignmentChange) {
        onAssignmentChange(newSelection);
      }

      return newSelection;
    });
  };

  // Select all filtered students
  const selectAllFiltered = () => {
    const filteredStudentIds = filteredStudents.map(s => s.id);
    const newSelection = [...new Set([...selectedStudentIds, ...filteredStudentIds])];
    setSelectedStudentIds(newSelection);

    if (onAssignmentChange) {
      onAssignmentChange(newSelection);
    }
  };

  // Deselect all filtered students
  const deselectAllFiltered = () => {
    const filteredStudentIds = filteredStudents.map(s => s.id);
    const newSelection = selectedStudentIds.filter(id => !filteredStudentIds.includes(id));
    setSelectedStudentIds(newSelection);

    if (onAssignmentChange) {
      onAssignmentChange(newSelection);
    }
  };

  // Check if all filtered students are selected
  const areAllFilteredSelected = filteredStudents.length > 0 &&
    filteredStudents.every(student => selectedStudentIds.includes(student.id));

  // Check if any filtered students are selected
  const areSomeFilteredSelected = filteredStudents.some(student =>
    selectedStudentIds.includes(student.id));

  // Get students being added/removed for display
  const getStudentName = (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.firstName} ${student.lastName}` : 'Unknown Student';
  };



  // Show confirmation dialog before saving
  const handleSaveClick = () => {
    // Calculate changes directly to avoid stale state
    const currentAdded = selectedStudentIds.filter(id => !savedStudentIds.includes(id));
    const currentRemoved = savedStudentIds.filter(id => !selectedStudentIds.includes(id));

    if (currentAdded.length === 0 && currentRemoved.length === 0) {
      toast({
        title: 'No Changes',
        description: 'No changes to save.',
      });
      return;
    }

    // Update pending changes and show dialog
    setPendingChanges({ added: currentAdded, removed: currentRemoved });
    setShowConfirmDialog(true);
  };

  // Save student assignments to the class
  const saveAssignments = async () => {
    try {
      setIsUpdating(true);
      setShowConfirmDialog(false);

      // Update the class with the selected students
      await classApi.updateStudents(classId, selectedStudentIds);

      // Update saved state to current selection and reset pending changes
      setSavedStudentIds([...selectedStudentIds]);
      setPendingChanges({ added: [], removed: [] });

      toast({
        title: 'Success',
        description: `Student assignments updated successfully. ${pendingChanges.added.length} added, ${pendingChanges.removed.length} removed.`,
      });

      // Notify parent component to refetch data
      if (onAssignmentSaved) {
        onAssignmentSaved();
      }
    } catch (error) {
      console.error('Error updating student assignments:', error);
      toast({
        title: 'Error',
        description: 'Failed to update student assignments. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search students..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            {/* Bulk selection controls */}
            {filteredStudents.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllFiltered}
                  disabled={areAllFilteredSelected || loading}
                >
                  <Users className="mr-2 h-4 w-4" />
                  Select All ({filteredStudents.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAllFiltered}
                  disabled={!areSomeFilteredSelected || loading}
                >
                  <UserMinus className="mr-2 h-4 w-4" />
                  Deselect All
                </Button>
              </>
            )}

            <Button
              onClick={handleSaveClick}
              disabled={isUpdating || loading}
              className={currentPendingChanges.added.length > 0 || currentPendingChanges.removed.length > 0 ? 'bg-orange-600 hover:bg-orange-700' : ''}
            >
              {isUpdating ? (
                <>
                  <InlineSpinner size="sm" className="mr-2" />
                  Saving assignments...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Save Assignments
                  {(currentPendingChanges.added.length > 0 || currentPendingChanges.removed.length > 0) && (
                    <span className="ml-2 bg-white text-orange-600 px-2 py-1 rounded-full text-xs">
                      {currentPendingChanges.added.length + currentPendingChanges.removed.length}
                    </span>
                  )}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Show pending changes summary */}
        {(currentPendingChanges.added.length > 0 || currentPendingChanges.removed.length > 0) && (
          <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
            <div className="text-sm text-orange-800">
              <strong>Pending Changes:</strong>
              {currentPendingChanges.added.length > 0 && (
                <span className="ml-2 text-green-700">+{currentPendingChanges.added.length} to add</span>
              )}
              {currentPendingChanges.removed.length > 0 && (
                <span className="ml-2 text-red-700">-{currentPendingChanges.removed.length} to remove</span>
              )}
            </div>
          </div>
        )}

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <InlineSpinner size="md" />
        </div>
      ) : students.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No students found for this batch.</p>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={areAllFilteredSelected}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        selectAllFiltered();
                      } else {
                        deselectAllFiltered();
                      }
                    }}
                  />
                </TableHead>
                <TableHead>Student ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStudents.map((student) => (
                <TableRow key={student.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedStudentIds.includes(student.id)}
                      onCheckedChange={() => toggleStudentSelection(student.id)}
                    />
                  </TableCell>
                  <TableCell>{student.studentId}</TableCell>
                  <TableCell>{student.firstName} {student.lastName}</TableCell>
                  <TableCell>{student.email}</TableCell>
                  <TableCell>
                    <StatusBadge status={student.status} variant="rounded" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Student Assignment Changes</DialogTitle>
            <DialogDescription>
              Please review the changes you're about to make to this class.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {pendingChanges.added.length > 0 && (
              <div>
                <h4 className="font-medium text-green-700 mb-2">
                  Students to Add ({pendingChanges.added.length}):
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {pendingChanges.added.map(studentId => (
                    <li key={studentId} className="text-green-600">
                      {getStudentName(studentId)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pendingChanges.removed.length > 0 && (
              <div>
                <h4 className="font-medium text-red-700 mb-2">
                  Students to Remove ({pendingChanges.removed.length}):
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {pendingChanges.removed.map(studentId => (
                    <li key={studentId} className="text-red-600">
                      {getStudentName(studentId)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={saveAssignments}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <InlineSpinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Confirm Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudentAssignment;
